// Single source of truth for the bundled avatar presets. The server persists the
// KEY (not a resolved URL) on each user; the client maps that key to a locally
// bundled asset here so it loads instantly with no API round-trip. A real
// uploaded/OAuth photo (user.avatar) still takes precedence over the preset.
//
// Assets are DiceBear "lorelei" (CC BY 4.0 — see /LICENSES). Keep these keys in
// sync with the server's avatarPresetEnum.
import cosmicAvatar from '@/assets/avatars/cosmic.svg';
import forestAvatar from '@/assets/avatars/forest.svg';
import neonAvatar from '@/assets/avatars/neon.svg';
import goldenAvatar from '@/assets/avatars/golden.svg';
import midnightAvatar from '@/assets/avatars/midnight.svg';

export type AvatarPresetKey =
  'cosmic' | 'forest' | 'neon' | 'golden' | 'midnight';

export const AVATAR_PRESET_KEYS: AvatarPresetKey[] = [
  'cosmic',
  'forest',
  'neon',
  'golden',
  'midnight',
];

export const AVATAR_PRESET_MAP: Record<AvatarPresetKey, string> = {
  cosmic: cosmicAvatar,
  forest: forestAvatar,
  neon: neonAvatar,
  golden: goldenAvatar,
  midnight: midnightAvatar,
};

// Resolve a preset key to its bundled asset URL, defaulting to 'cosmic' (the
// server's column default) when the key is missing.
export function resolveAvatarPreset(
  key: AvatarPresetKey | null | undefined,
): string {
  return AVATAR_PRESET_MAP[key ?? 'cosmic'];
}

// The one place that decides which avatar actually shows: the real photo or the
// preset. Both are always stored; `useAvatarPreset` records the user's choice so
// they can switch back and forth. Falls back to the preset when there's no photo.
export function resolveUserAvatar(user: {
  avatar?: string | null;
  avatarPreset?: AvatarPresetKey | null;
  useAvatarPreset?: boolean | null;
}): string {
  if (!user.useAvatarPreset && user.avatar) return user.avatar;
  return resolveAvatarPreset(user.avatarPreset);
}
